/**
 * Report theme and presentation helpers shared by the report section
 * components: palette, legend overrides, label formatter and hidden-section
 * config.
 */
import { getRuntimeSetting } from '../../config/runtimeSettings';

// Remchannel Colour Palette
const REMCHANNEL_COLORS = {
  steelBlue: "#003366",
  green: "#00A651",
  black: "#000000",
  paper: "#FFFFFF",
  orange: "#FF6B00",
  rust: "#C35214",
  blue: "#1B3664",
  grey: "#666666",
  // Tints/Shades
  steelBlue80: "#33587A",
  steelBlue60: "#668BAF",
  steelBlue40: "#99BFD4",
  steelBlue20: "#CCDFF0",
  green80: "#33B86B",
  green60: "#66CB8C",
  green40: "#99DEAD",
  green20: "#CCF1CE",
  orange80: "#FF8A33",
  orange60: "#FFA866",
  orange40: "#FFC699",
  orange20: "#FFE4CC",
  rust80: "#D16B33",
  rust60: "#E18566",
  rust40: "#F1A099",
  rust20: "#F9CABB",
  blue80: "#495A7A",
  blue60: "#7A8BAF",
  blue40: "#AABFD4",
  blue20: "#D5DFF0",
  grey80: "#888888",
  grey60: "#AAAAAA",
  grey40: "#CCCCCC",
  grey20: "#EEEEEE",
  // Other high-contrast colors (distinct from palette above)
  contrastAqua: "#00FFFF",
  contrastMagenta: "#FF00FF",
  contrastLime: "#32CD32",
  contrastCoral: "#FF6347",
  contrastNavy: "#00008B",
  contrastGold: "#FFD700",
};

/** Known acronym/abbreviation overrides for proper casing */
const LEGEND_LABEL_OVERRIDES = {
  'fsp': 'FSP',
  'sars': 'SARs',
};

/** Format legend/label text: remove underscores, title-case each word */
const formatLegendLabel = (str) => {
  if (str == null || typeof str !== "string") return str ?? "";
  return str
    .replace(/_/g, " ")
    .split(" ")
    .map(word => LEGEND_LABEL_OVERRIDES[word.toLowerCase()] || (word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
};

/**
 * Parse hidden sections configuration from environment variable
 * Returns an object with boolean flags for each section
 * Supports both build-time (process.env) and runtime (window.ENV) configuration
 */
const getHiddenSections = () => {
  // Try runtime config first (window.ENV), then build-time config (process.env)
  const hiddenSectionsEnv = getRuntimeSetting('REPORT_EXPORT_HIDDEN_SECTIONS', '{}') || '{}';
  try {
    const config = typeof hiddenSectionsEnv === 'string' ? JSON.parse(hiddenSectionsEnv) : hiddenSectionsEnv;
    // Default to true (hidden) unless explicitly set to false
    return {
      ltiParticipationBaseSalary: config.ltiParticipationBaseSalary !== false,
      ltiParticipationTGP: config.ltiParticipationTGP !== false,
      ltiAnnualAwards: config.ltiAnnualAwards !== false,
      ltiAllocationPercentagesByGrade: config.ltiAllocationPercentagesByGrade !== false,
      stiParticipationBaseSalary: config.stiParticipationBaseSalary !== false,
      stiParticipationTGP: config.stiParticipationTGP !== false,
      stiAllocationPercentagesByRemunerationBase: config.stiAllocationPercentagesByRemunerationBase !== false,
      stiAllocationPercentagesByGrade: config.stiAllocationPercentagesByGrade !== false,
      totalRemunerationPayMixOldVersion: config.totalRemunerationPayMixOldVersion !== false,
    };
  } catch (error) {
    console.warn('Failed to parse REPORT_EXPORT_HIDDEN_SECTIONS, using defaults (all sections hidden):', error.message);
    // Default to true (hidden) for all sections
    return {
      ltiParticipationBaseSalary: true,
      ltiParticipationTGP: true,
      ltiAnnualAwards: true,
      ltiAllocationPercentagesByGrade: true,
      stiParticipationBaseSalary: true,
      stiParticipationTGP: true,
      stiAllocationPercentagesByRemunerationBase: true,
      stiAllocationPercentagesByGrade: true,
      totalRemunerationPayMixOldVersion: true,
    };
  }
};

/** Shared palette for every chart. */
export const CHART_PALETTE = [
  REMCHANNEL_COLORS.steelBlue,
  REMCHANNEL_COLORS.green,
  REMCHANNEL_COLORS.orange,
  REMCHANNEL_COLORS.rust,
  REMCHANNEL_COLORS.blue,
  REMCHANNEL_COLORS.grey,
];

export { REMCHANNEL_COLORS, LEGEND_LABEL_OVERRIDES, formatLegendLabel, getHiddenSections };
